import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Stack,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ScienceIcon from "@mui/icons-material/Science";
import SendIcon from "@mui/icons-material/Send";
import CampaignIcon from "@mui/icons-material/Campaign";
import GroupIcon from "@mui/icons-material/Group";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TuneIcon from "@mui/icons-material/Tune";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchAlerts,
  fetchAlertsMeta,
  fetchAlertGroups,
  createAlert,
  updateAlert,
  deleteAlert,
  testAlert,
  createAlertGroup,
  updateAlertGroup,
  deleteAlertGroup,
  selectAlerts,
  selectAlertGroups,
  selectAlertsMeta,
  selectAlertsLoading,
} from "../../../store/slices/alertsSlice";
import { pushOwnerNotification, fetchNotifications } from "../../../store/slices/notificationsSlice";
import { getActiveUsersList } from "../../../store/slices/usersSlice";
import { setUsers } from "../../../store/slices/usersSlice";
import { getUser } from "../../../store/slices/userSlice";
import { formatUserDisplayName } from "../../../utils/userDisplayName";
import { GET } from "../../../configs/api";
import { api } from "../../../utils/constants";
import {
  getModernFieldSurface,
  getModernTextFieldSx,
} from "../jobs/modernFormFieldStyles";

const DAY_OPTIONS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const WINDOW_LABELS = {
  calendar_yesterday: "Yesterday (calendar day)",
  calendar_today: "Today so far",
  rolling_hours: "Rolling hours",
  rolling_days: "Rolling days",
  past_week: "Past 7 days",
  past_month: "Past month",
};

const SECTION_LABELS = {
  jobsSummary: "Jobs summary",
  jobsByStatus: "Pipeline by status",
  financials: "Financials",
  technicians: "Technicians",
  topClients: "Top clients",
  recentActivity: "Recent activity",
  aiSummary: "AI briefing",
};

const DIGEST_SECTION_KEYS = [
  "jobsSummary",
  "jobsByStatus",
  "financials",
  "technicians",
  "topClients",
  "recentActivity",
];

const FormSection = ({ icon, title, subtitle, children, surface }) => (
  <Box
    sx={{
      p: { xs: 1.5, sm: 2 },
      borderRadius: { xs: "14px", sm: "16px" },
      border: "1px solid",
      borderColor: surface.panelBorder,
      backgroundImage: surface.panelBg,
      boxShadow: surface.panelShadow,
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: { xs: 1.5, sm: 1.75 } }}>
      <Box
        sx={{
          width: { xs: 32, sm: 34 },
          height: { xs: 32, sm: 34 },
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          bgcolor: surface.iconBg,
          color: surface.accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: "0.9rem", sm: "0.95rem" }, letterSpacing: "-0.01em" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "0.78rem", color: surface.muted, mt: 0.15, lineHeight: 1.35 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    {children}
  </Box>
);

const SelectableChip = ({ selected, label, onClick, surface }) => (
  <Chip
    clickable
    onClick={onClick}
    label={label}
    variant={selected ? "filled" : "outlined"}
    sx={{
      fontWeight: 600,
      borderRadius: "10px",
      height: { xs: 38, sm: 34 },
      px: 0.25,
      borderColor: selected ? surface.accent : surface.fieldBorder,
      bgcolor: selected
        ? (surface.isDark ? alpha("#e2e8f0", 0.16) : alpha("#0f172a", 0.08))
        : "transparent",
      color: selected ? surface.accent : "text.primary",
      transition: "transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease",
      "&:hover": {
        bgcolor: selected
          ? (surface.isDark ? alpha("#e2e8f0", 0.22) : alpha("#0f172a", 0.12))
          : (surface.isDark ? alpha("#e2e8f0", 0.06) : alpha("#0f172a", 0.04)),
        borderColor: surface.accent,
        transform: "translateY(-1px)",
      },
    }}
  />
);

const emptyAlertForm = (meta) => ({
  name: "",
  enabled: true,
  timezone: "America/New_York",
  sendTime: "08:00",
  daysOfWeek: [1, 2, 3, 4, 5],
  windowMode: "calendar_yesterday",
  windowValue: { hours: 24, days: 7 },
  channels: ["email"],
  config: {
    sections: { ...(meta?.defaultSections || {
      jobsSummary: true,
      jobsByStatus: true,
      financials: true,
      technicians: true,
      topClients: true,
      recentActivity: false,
      aiSummary: false,
    }) },
  },
  recipientUserIds: [],
  recipientGroupIds: [],
});

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function Alerts() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const alerts = useSelector(selectAlerts);
  const groups = useSelector(selectAlertGroups);
  const meta = useSelector(selectAlertsMeta);
  const loading = useSelector(selectAlertsLoading);
  const usersList = useSelector(getActiveUsersList) || [];

  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [busy, setBusy] = useState(false);

  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [alertForm, setAlertForm] = useState(() => emptyAlertForm(null));

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", memberIds: [] });

  const [announceText, setAnnounceText] = useState("");
  const [broadcastAll, setBroadcastAll] = useState(false);
  const [announceRecipients, setAnnounceRecipients] = useState([]);
  const [announceGroupIds, setAnnounceGroupIds] = useState([]);
  const [pushSending, setPushSending] = useState(false);

  useEffect(() => {
    dispatch(fetchAlertsMeta());
    dispatch(fetchAlerts());
    dispatch(fetchAlertGroups());
  }, [dispatch]);

  // Ensure company users are available for recipient pickers (MainDrawer usually
  // loads them via /clients; refetch /users if the list is still empty).
  useEffect(() => {
    let cancelled = false;
    const ensureUsers = async () => {
      if ((usersList || []).length > 0) return;
      const { data, status } = await GET(api.getUsers);
      if (cancelled || status !== 200) return;
      const list = Array.isArray(data) ? data : data?.users || [];
      if (list.length) dispatch(setUsers({ list }));
    };
    ensureUsers();
    return () => { cancelled = true; };
  }, [dispatch, usersList]);

  const userOptions = useMemo(
    () =>
      (usersList || [])
        .filter((u) => u.is_active !== false)
        .map((u) => ({
          id: u.id,
          label: formatUserDisplayName(u) || u.email,
          email: u.email,
        })),
    [usersList],
  );

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const openCreateAlert = () => {
    setEditingAlert(null);
    setAlertForm(emptyAlertForm(meta));
    setAlertDialogOpen(true);
  };

  const openEditAlert = (alert) => {
    setEditingAlert(alert);
    setAlertForm({
      name: alert.name,
      enabled: alert.enabled,
      timezone: alert.timezone,
      sendTime: String(alert.sendTime || "08:00").slice(0, 5),
      daysOfWeek: alert.daysOfWeek || [1, 2, 3, 4, 5],
      windowMode: alert.windowMode,
      windowValue: {
        hours: alert.windowValue?.hours || 24,
        days: alert.windowValue?.days || 7,
      },
      channels: alert.channels || ["email"],
      config: {
        sections: { ...emptyAlertForm(meta).config.sections, ...(alert.config?.sections || {}) },
      },
      recipientUserIds: alert.recipientUserIds || [],
      recipientGroupIds: alert.recipientGroupIds || [],
    });
    setAlertDialogOpen(true);
  };

  const saveAlert = async () => {
    if (!alertForm.name.trim()) {
      showToast("error", "Name is required");
      return;
    }
    if (
      alertForm.recipientUserIds.length === 0
      && alertForm.recipientGroupIds.length === 0
    ) {
      showToast("error", "Select at least one recipient user or group");
      return;
    }
    if (!alertForm.daysOfWeek.length) {
      showToast("error", "Select at least one day");
      return;
    }
    setBusy(true);
    const body = {
      ...alertForm,
      type: "daily_report",
      sendTime: alertForm.sendTime.length === 5 ? `${alertForm.sendTime}:00` : alertForm.sendTime,
      windowValue:
        alertForm.windowMode === "rolling_hours"
          ? { hours: Number(alertForm.windowValue.hours) || 24 }
          : alertForm.windowMode === "rolling_days"
            ? { days: Number(alertForm.windowValue.days) || 7 }
            : {},
      config: {
        sections: alertForm.config.sections,
        includeAiSummary: Boolean(alertForm.config.sections?.aiSummary),
      },
    };
    try {
      if (editingAlert) {
        await dispatch(updateAlert({ id: editingAlert.id, body })).unwrap();
        showToast("success", "Alert updated");
      } else {
        await dispatch(createAlert(body)).unwrap();
        showToast("success", "Alert created");
      }
      setAlertDialogOpen(false);
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Failed to save alert");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAlert = async (id) => {
    if (!window.confirm("Delete this alert?")) return;
    try {
      await dispatch(deleteAlert(id)).unwrap();
      showToast("success", "Alert deleted");
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Failed to delete");
    }
  };

  const handleToggleEnabled = async (alert) => {
    try {
      await dispatch(updateAlert({ id: alert.id, body: { enabled: !alert.enabled } })).unwrap();
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Failed to update");
    }
  };

  const handleTest = async (alert) => {
    setBusy(true);
    try {
      await dispatch(testAlert(alert.id)).unwrap();
      const hasInApp = (alert.channels || []).includes("in_app");
      if (hasInApp) {
        dispatch(fetchNotifications());
      }
      showToast(
        "success",
        hasInApp
          ? "Test sent to your inbox and in-app notifications"
          : "Test sent to your inbox",
      );
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: "", description: "", memberIds: [] });
    setGroupDialogOpen(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || "",
      memberIds: group.memberIds || [],
    });
    setGroupDialogOpen(true);
  };

  const saveGroup = async () => {
    if (!groupForm.name.trim()) {
      showToast("error", "Group name is required");
      return;
    }
    setBusy(true);
    try {
      if (editingGroup) {
        await dispatch(updateAlertGroup({ id: editingGroup.id, body: groupForm })).unwrap();
        showToast("success", "Group updated");
      } else {
        await dispatch(createAlertGroup(groupForm)).unwrap();
        showToast("success", "Group created");
      }
      setGroupDialogOpen(false);
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Failed to save group");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("Delete this mailing group?")) return;
    try {
      await dispatch(deleteAlertGroup(id)).unwrap();
      showToast("success", "Group deleted");
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Failed to delete group");
    }
  };

  const expandedAnnounceUserIds = useMemo(() => {
    const fromGroups = groups
      .filter((g) => announceGroupIds.includes(g.id))
      .flatMap((g) => g.memberIds || []);
    return [...new Set([...announceRecipients.map((u) => u.id), ...fromGroups])].filter(
      (id) => id !== user?.id,
    );
  }, [announceRecipients, announceGroupIds, groups, user?.id]);

  const canSendAnnounce =
    announceText.trim().length > 0 &&
    (broadcastAll || expandedAnnounceUserIds.length > 0);

  const sendAnnounce = async () => {
    if (!canSendAnnounce) return;
    setPushSending(true);
    try {
      const payload = broadcastAll
        ? { text: announceText.trim(), broadcast: true }
        : { text: announceText.trim(), userIds: expandedAnnounceUserIds };
      const data = await dispatch(pushOwnerNotification(payload)).unwrap();
      showToast(
        "success",
        `Sent to ${data?.recipientCount ?? data?.createdCount ?? "team"}`,
      );
      setAnnounceText("");
      setAnnounceRecipients([]);
      setAnnounceGroupIds([]);
      setBroadcastAll(false);
    } catch (err) {
      showToast("error", typeof err === "string" ? err : "Failed to send");
    } finally {
      setPushSending(false);
    }
  };

  const fieldSurface = useMemo(() => {
    const base = getModernFieldSurface(isDark);
    return {
      ...base,
      isDark,
      iconBg: isDark ? alpha("#e2e8f0", 0.08) : alpha("#0f172a", 0.05),
      panelShadow: isDark
        ? "0 0 0 1px rgba(255,255,255,0.03), 0 12px 28px -22px rgba(0,0,0,0.55)"
        : "0 10px 28px -20px rgba(15, 23, 42, 0.14)",
    };
  }, [isDark]);

  const fieldSx = useMemo(() => getModernTextFieldSx(isDark), [isDark]);

  const solidBtnSx = useMemo(() => ({
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    letterSpacing: "0.01em",
    px: 2,
    color: isDark ? "#0f172a" : "#ffffff",
    bgcolor: isDark ? "#e2e8f0" : "#0f172a",
    boxShadow: isDark
      ? "0 8px 20px -12px rgba(0,0,0,0.55)"
      : "0 8px 20px -12px rgba(15,23,42,0.45)",
    "&:hover": {
      bgcolor: isDark ? "#f8fafc" : "#000000",
      boxShadow: isDark
        ? "0 10px 24px -10px rgba(0,0,0,0.65)"
        : "0 10px 24px -10px rgba(15,23,42,0.55)",
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.7)",
      bgcolor: isDark ? "#64748b" : "#94a3b8",
      boxShadow: "none",
    },
  }), [isDark]);

  const textBtnSx = useMemo(() => ({
    textTransform: "none",
    fontWeight: 700,
    borderRadius: "10px",
    color: isDark ? "#e2e8f0" : "#0f172a",
    "&:hover": {
      bgcolor: isDark ? alpha("#e2e8f0", 0.08) : alpha("#0f172a", 0.05),
    },
  }), [isDark]);

  const switchSx = useMemo(() => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: isDark ? "#e2e8f0" : "#0f172a",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: isDark ? "#e2e8f0" : "#0f172a",
    },
  }), [isDark]);

  const tabsSx = useMemo(() => ({
    minHeight: 48,
    p: 0.5,
    mb: 2,
    borderRadius: "14px",
    border: "1px solid",
    borderColor: fieldSurface.panelBorder,
    bgcolor: isDark ? alpha("#e2e8f0", 0.04) : alpha("#0f172a", 0.03),
    "& .MuiTabs-flexContainer": {
      gap: 0.5,
    },
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.875rem",
      minHeight: 40,
      borderRadius: "10px",
      color: fieldSurface.muted,
      px: { xs: 1.25, sm: 2 },
      transition: "background-color 0.15s ease, color 0.15s ease",
      "& .MuiTab-iconWrapper": {
        marginRight: "6px !important",
        marginBottom: "0 !important",
      },
      "&.Mui-selected": {
        color: isDark ? "#0f172a" : "#ffffff",
        bgcolor: isDark ? "#e2e8f0" : "#0f172a",
      },
    },
  }), [isDark, fieldSurface]);

  const paperSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: "16px",
    border: "1px solid",
    borderColor: fieldSurface.panelBorder,
    backgroundImage: fieldSurface.panelBg,
    boxShadow: fieldSurface.panelShadow,
    transition: "border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease",
  };

  const listCardSx = {
    ...paperSx,
    cursor: "pointer",
    "&:hover": {
      borderColor: fieldSurface.accent,
      transform: "translateY(-1px)",
      boxShadow: isDark
        ? "0 0 0 1px rgba(255,255,255,0.05), 0 14px 32px -20px rgba(0,0,0,0.7)"
        : "0 12px 28px -18px rgba(15, 23, 42, 0.2)",
    },
  };

  const metaChipSx = {
    height: 26,
    fontWeight: 600,
    fontSize: "0.72rem",
    borderRadius: "8px",
    borderColor: fieldSurface.fieldBorder,
    color: fieldSurface.muted,
    bgcolor: isDark ? alpha("#e2e8f0", 0.04) : alpha("#0f172a", 0.02),
  };

  const summarizeAlertSections = (alert) => {
    const sections = alert.config?.sections || {};
    const active = DIGEST_SECTION_KEYS.filter((key) => sections[key]);
    const aiOn = Boolean(sections.aiSummary || alert.config?.includeAiSummary);
    return { active, aiOn };
  };

  const formatAlertDays = (daysOfWeek = []) =>
    daysOfWeek
      .map((d) => DAY_OPTIONS.find((o) => o.value === d)?.label)
      .filter(Boolean)
      .join(" ");

  const channelLabel = (ch) => (ch === "in_app" ? "In-app" : ch === "email" ? "Email" : ch);

  const aiEnabled = Boolean(alertForm.config.sections?.aiSummary);

  const toggleSection = (key) => {
    setAlertForm((f) => ({
      ...f,
      config: {
        ...f.config,
        sections: { ...f.config.sections, [key]: !f.config.sections?.[key] },
      },
    }));
  };

  const toggleChannel = (ch) => {
    setAlertForm((f) => {
      const next = f.channels.includes(ch)
        ? f.channels.filter((c) => c !== ch)
        : [...f.channels, ch];
      return { ...f, channels: next.length ? next : ["email"] };
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color: isDark ? "#0f172a" : "#fff",
            backgroundImage: isDark
              ? "linear-gradient(135deg, #e2e8f0 0%, #f8fafc 55%, #cbd5e1 100%)"
              : "linear-gradient(135deg, #0f172a 0%, #1e293b 48%, #000000 100%)",
            boxShadow: isDark
              ? "0 8px 20px -10px rgba(0,0,0,0.55)"
              : "0 8px 20px -10px rgba(15,23,42,0.45)",
          }}
        >
          <CampaignIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}
          >
            Alerts
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              fontSize: "0.875rem",
              color: fieldSurface.muted,
              maxWidth: 560,
              lineHeight: 1.45,
            }}
          >
            Schedule business digests, build mailing lists, and push announcements to your team.
          </Typography>
        </Box>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant={isMobile ? "fullWidth" : "standard"}
        sx={tabsSx}
      >
        <Tab icon={<AssessmentIcon fontSize="small" />} iconPosition="start" label="Reports" />
        <Tab icon={<CampaignIcon fontSize="small" />} iconPosition="start" label="Announce" />
        <Tab icon={<GroupIcon fontSize="small" />} iconPosition="start" label="Groups" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.25}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.01em" }}>
              Scheduled reports
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: fieldSurface.muted, mt: 0.2 }}>
              Digests on the days and time you choose
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateAlert}
            sx={{ ...solidBtnSx, alignSelf: { xs: "stretch", sm: "center" } }}
          >
            New report
          </Button>
        </Stack>

        {loading && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={28} sx={{ color: fieldSurface.accent }} />
          </Box>
        )}

        {!loading && alerts.length === 0 && (
          <Paper sx={paperSx}>
            <Typography sx={{ color: fieldSurface.muted }}>
              No reports yet. Create one to email a Control Center–style digest on the days and time you choose.
            </Typography>
          </Paper>
        )}

        <Stack spacing={1.75}>
          {alerts.map((alert) => {
            const { active: activeSections, aiOn } = summarizeAlertSections(alert);
            const peopleCount = alert.recipientUserIds?.length || 0;
            const groupCount = alert.recipientGroupIds?.length || 0;
            const daysLabel = formatAlertDays(alert.daysOfWeek);
            const windowLabel = WINDOW_LABELS[alert.windowMode] || alert.windowMode;
            const channels = (alert.channels || []).map(channelLabel);

            return (
              <Paper
                key={alert.id}
                role="button"
                tabIndex={0}
                aria-label={`Edit report ${alert.name}`}
                onClick={() => openEditAlert(alert)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openEditAlert(alert);
                  }
                }}
                sx={{
                  ...listCardSx,
                  p: { xs: 2.25, sm: 2.75 },
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ md: "flex-start" }}
                  spacing={2}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: "1.05rem", sm: "1.15rem" },
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {alert.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={alert.enabled ? "On" : "Off"}
                        sx={{
                          height: 26,
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          bgcolor: alert.enabled
                            ? (isDark ? alpha("#4ade80", 0.16) : alpha("#16a34a", 0.12))
                            : (isDark ? alpha("#e2e8f0", 0.08) : alpha("#0f172a", 0.06)),
                          color: alert.enabled
                            ? (isDark ? "#86efac" : "#15803d")
                            : fieldSurface.muted,
                          border: "none",
                        }}
                      />
                      {aiOn && (
                        <Chip
                          size="small"
                          icon={<AutoAwesomeIcon sx={{ fontSize: "14px !important" }} />}
                          label="AI"
                          sx={{
                            height: 26,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            border: "1px solid",
                            borderColor: fieldSurface.fieldBorder,
                            color: fieldSurface.accent,
                            bgcolor: isDark ? alpha("#e2e8f0", 0.08) : alpha("#0f172a", 0.04),
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      )}
                    </Stack>

                    <Stack spacing={1.35} sx={{ mt: 1.75 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <ScheduleIcon sx={{ fontSize: 18, mt: "2px", color: fieldSurface.muted }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: fieldSurface.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            Schedule
                          </Typography>
                          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, mt: 0.2 }}>
                            {String(alert.sendTime || "").slice(0, 5)} · {daysLabel || "No days"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.8rem", color: fieldSurface.muted, mt: 0.15 }}>
                            {alert.timezone} · Window: {windowLabel}
                            {alert.windowMode === "rolling_hours" && alert.windowValue?.hours
                              ? ` (${alert.windowValue.hours}h)`
                              : ""}
                            {alert.windowMode === "rolling_days" && alert.windowValue?.days
                              ? ` (${alert.windowValue.days}d)`
                              : ""}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TuneIcon sx={{ fontSize: 18, mt: "2px", color: fieldSurface.muted }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: fieldSurface.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            Include
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ mt: 0.65 }}>
                            {activeSections.length === 0 && !aiOn ? (
                              <Typography sx={{ fontSize: "0.8rem", color: fieldSurface.muted }}>
                                No sections selected
                              </Typography>
                            ) : (
                              <>
                                {activeSections.map((key) => (
                                  <Chip
                                    key={key}
                                    size="small"
                                    label={SECTION_LABELS[key]}
                                    variant="outlined"
                                    sx={metaChipSx}
                                  />
                                ))}
                                {aiOn && (
                                  <Chip
                                    size="small"
                                    icon={<AutoAwesomeIcon sx={{ fontSize: "14px !important" }} />}
                                    label="AI briefing"
                                    variant="outlined"
                                    sx={{
                                      ...metaChipSx,
                                      color: fieldSurface.accent,
                                      "& .MuiChip-icon": { color: "inherit" },
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Stack>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 1.25, sm: 3 }}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <SendIcon sx={{ fontSize: 18, mt: "2px", color: fieldSurface.muted }} />
                          <Box>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: fieldSurface.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Channels
                            </Typography>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, mt: 0.2 }}>
                              {channels.length ? channels.join(" + ") : "None"}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <PeopleAltIcon sx={{ fontSize: 18, mt: "2px", color: fieldSurface.muted }} />
                          <Box>
                            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: fieldSurface.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Recipients
                            </Typography>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, mt: 0.2 }}>
                              {peopleCount} {peopleCount === 1 ? "person" : "people"}
                              {" · "}
                              {groupCount} {groupCount === 1 ? "group" : "groups"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      <Typography sx={{ fontSize: "0.78rem", color: fieldSurface.muted, pt: 0.25 }}>
                        {alert.lastRunAt
                          ? `Last run ${new Date(alert.lastRunAt).toLocaleString()} (${alert.lastRunStatus || "—"})`
                          : "Not run yet · click to edit"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    flexWrap="wrap"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ flexShrink: 0, cursor: "default" }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(alert.enabled)}
                          onChange={() => handleToggleEnabled(alert)}
                          size="small"
                          sx={switchSx}
                        />
                      }
                      label="Enabled"
                      sx={{
                        mr: 0.5,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: fieldSurface.muted,
                        },
                      }}
                    />
                    <Tooltip title="Send test to yourself">
                      <IconButton onClick={() => handleTest(alert)} disabled={busy} sx={{ color: fieldSurface.accent }}>
                        <ScienceIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit report">
                      <IconButton onClick={() => openEditAlert(alert)} sx={{ color: fieldSurface.accent }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete report">
                      <IconButton color="error" onClick={() => handleDeleteAlert(alert.id)}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Paper sx={paperSx}>
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                display: "grid",
                placeItems: "center",
                bgcolor: fieldSurface.iconBg,
                color: fieldSurface.accent,
                flexShrink: 0,
              }}
            >
              <CampaignIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.01em" }}>
                Team announcement
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: fieldSurface.muted, mt: 0.2, lineHeight: 1.4 }}>
                Push an in-app message (email mirrors when recipients have email notifications on).
              </Typography>
            </Box>
          </Stack>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Message"
            value={announceText}
            onChange={(e) => setAnnounceText(e.target.value.slice(0, 4000))}
            helperText={`${announceText.length}/4000`}
            sx={{ ...fieldSx, mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={broadcastAll}
                onChange={(e) => setBroadcastAll(e.target.checked)}
                sx={switchSx}
              />
            }
            label="Everyone (broadcast)"
            sx={{
              mb: 1.5,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
                fontWeight: 600,
              },
            }}
          />
          {!broadcastAll && (
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Autocomplete
                multiple
                options={userOptions}
                value={announceRecipients}
                onChange={(_, v) => setAnnounceRecipients(v)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="People" placeholder="Select users" sx={fieldSx} />
                )}
              />
              <Autocomplete
                multiple
                options={groups}
                value={groups.filter((g) => announceGroupIds.includes(g.id))}
                onChange={(_, v) => setAnnounceGroupIds(v.map((g) => g.id))}
                getOptionLabel={(o) => `${o.name} (${o.memberCount ?? o.memberIds?.length ?? 0})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="Groups" placeholder="Add mailing lists" sx={fieldSx} />
                )}
              />
            </Stack>
          )}
          <Button
            variant="contained"
            startIcon={pushSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            disabled={!canSendAnnounce || pushSending}
            onClick={sendAnnounce}
            sx={{ ...solidBtnSx, minHeight: { xs: 44, sm: 40 }, width: { xs: "100%", sm: "auto" } }}
          >
            {pushSending ? "Sending…" : "Send announcement"}
          </Button>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1.25}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", letterSpacing: "-0.01em" }}>
              Mailing lists
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: fieldSurface.muted, mt: 0.2 }}>
              Reusable recipient groups for reports and announcements
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateGroup}
            sx={{ ...solidBtnSx, alignSelf: { xs: "stretch", sm: "center" } }}
          >
            New group
          </Button>
        </Stack>
        <Stack spacing={1.5}>
          {groups.map((group) => (
            <Paper key={group.id} sx={listCardSx}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} sx={{ letterSpacing: "-0.01em" }}>
                    {group.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: fieldSurface.muted, mt: 0.35 }}>
                    {group.memberCount ?? group.memberIds?.length ?? 0} members
                    {group.description ? ` · ${group.description}` : ""}
                  </Typography>
                </Box>
                <Stack direction="row">
                  <IconButton onClick={() => openEditGroup(group)} sx={{ color: fieldSurface.accent }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDeleteGroup(group.id)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
          {groups.length === 0 && (
            <Paper sx={paperSx}>
              <Typography sx={{ color: fieldSurface.muted }}>
                Create reusable recipient groups for reports and announcements.
              </Typography>
            </Paper>
          )}
        </Stack>
      </TabPanel>

      {/* Alert dialog */}
      <Dialog
        open={alertDialogOpen}
        onClose={() => !busy && setAlertDialogOpen(false)}
        fullWidth
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "18px",
            border: "1px solid",
            borderColor: fieldSurface.panelBorder,
            backgroundImage: isDark
              ? "linear-gradient(160deg, #1e293b 0%, #0f172a 45%, #020617 100%)"
              : "linear-gradient(160deg, #ffffff 0%, #f8fafc 52%, #f1f5f9 100%)",
            overflow: "hidden",
            m: isMobile ? 0 : undefined,
            maxHeight: isMobile ? "100%" : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            letterSpacing: "-0.02em",
            pb: 1,
            pr: isMobile ? 6 : 3,
            position: "relative",
            background: isDark
              ? "linear-gradient(180deg, rgba(30,41,59,0.9) 0%, transparent 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, transparent 100%)",
          }}
        >
          <Typography component="span" sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem" }, fontWeight: 800, display: "block" }}>
            {editingAlert ? "Edit report" : "New report"}
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: fieldSurface.muted, mt: 0.35, pr: isMobile ? 1 : 0 }}>
            {editingAlert
              ? "Update schedule, content, and who gets this digest."
              : "Pick a schedule, choose what to include, and add recipients."}
          </Typography>
          {isMobile && (
            <IconButton
              aria-label="Close"
              onClick={() => !busy && setAlertDialogOpen(false)}
              disabled={busy}
              sx={{ position: "absolute", right: 8, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            borderColor: fieldSurface.panelBorder,
            bgcolor: "transparent",
            px: { xs: 1.5, sm: 3 },
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Stack spacing={2} sx={{ pt: 1.25 }}>
            <FormSection
              surface={fieldSurface}
              icon={<ScheduleIcon fontSize="small" />}
              title="Schedule"
              subtitle="When this digest goes out"
            >
              <Stack spacing={2}>
                <TextField
                  label="Report name"
                  placeholder="e.g. Morning ops digest"
                  fullWidth
                  value={alertForm.name}
                  onChange={(e) => setAlertForm((f) => ({ ...f, name: e.target.value }))}
                  sx={fieldSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    label="Timezone"
                    fullWidth
                    value={alertForm.timezone}
                    onChange={(e) => setAlertForm((f) => ({ ...f, timezone: e.target.value }))}
                    sx={fieldSx}
                  >
                    {(meta?.timezones || ["America/New_York", "UTC"]).map((tz) => (
                      <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Send time"
                    type="time"
                    fullWidth
                    value={alertForm.sendTime}
                    onChange={(e) => setAlertForm((f) => ({ ...f, sendTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                </Stack>

                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: fieldSurface.muted,
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Days
                  </Typography>
                  <ToggleButtonGroup
                    value={alertForm.daysOfWeek}
                    onChange={(_, next) => {
                      if (!next || next.length === 0) return;
                      setAlertForm((f) => ({
                        ...f,
                        daysOfWeek: [...next].sort((a, b) => a - b),
                      }));
                    }}
                    size="small"
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                      gap: 0.6,
                      width: "100%",
                      "& .MuiToggleButtonGroup-grouped": {
                        border: "1px solid !important",
                        borderRadius: "10px !important",
                        mx: 0,
                        borderColor: `${fieldSurface.fieldBorder} !important`,
                      },
                    }}
                  >
                    {DAY_OPTIONS.map((d) => {
                      const selected = alertForm.daysOfWeek.includes(d.value);
                      return (
                        <ToggleButton
                          key={d.value}
                          value={d.value}
                          sx={{
                            px: { xs: 0.25, sm: 1.2 },
                            py: 0,
                            minWidth: 0,
                            minHeight: { xs: 44, sm: 38 },
                            width: "100%",
                            fontWeight: 700,
                            fontSize: { xs: "0.72rem", sm: "0.8125rem" },
                            textTransform: "none",
                            color: selected ? fieldSurface.accent : "text.secondary",
                            bgcolor: selected
                              ? (isDark ? alpha("#e2e8f0", 0.14) : alpha("#0f172a", 0.08))
                              : "transparent",
                            "&.Mui-selected": {
                              bgcolor: isDark ? alpha("#e2e8f0", 0.14) : alpha("#0f172a", 0.08),
                              color: fieldSurface.accent,
                              borderColor: `${fieldSurface.accent} !important`,
                              "&:hover": {
                                bgcolor: isDark ? alpha("#e2e8f0", 0.2) : alpha("#0f172a", 0.12),
                              },
                            },
                            "&:hover": {
                              bgcolor: isDark ? alpha("#e2e8f0", 0.08) : alpha("#0f172a", 0.04),
                            },
                          }}
                        >
                          {d.label}
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    label="Report window"
                    fullWidth
                    value={alertForm.windowMode}
                    onChange={(e) => setAlertForm((f) => ({ ...f, windowMode: e.target.value }))}
                    sx={fieldSx}
                  >
                    {Object.entries(WINDOW_LABELS).map(([k, label]) => (
                      <MenuItem key={k} value={k}>{label}</MenuItem>
                    ))}
                  </TextField>
                  {alertForm.windowMode === "rolling_hours" && (
                    <TextField
                      label="Hours"
                      type="number"
                      fullWidth
                      value={alertForm.windowValue.hours}
                      onChange={(e) =>
                        setAlertForm((f) => ({
                          ...f,
                          windowValue: { ...f.windowValue, hours: e.target.value },
                        }))
                      }
                      sx={fieldSx}
                    />
                  )}
                  {alertForm.windowMode === "rolling_days" && (
                    <TextField
                      label="Days"
                      type="number"
                      fullWidth
                      value={alertForm.windowValue.days}
                      onChange={(e) =>
                        setAlertForm((f) => ({
                          ...f,
                          windowValue: { ...f.windowValue, days: e.target.value },
                        }))
                      }
                      sx={fieldSx}
                    />
                  )}
                </Stack>
              </Stack>
            </FormSection>

            <FormSection
              surface={fieldSurface}
              icon={<TuneIcon fontSize="small" />}
              title="Content & delivery"
              subtitle="What lands in the digest and how it arrives"
            >
              <Stack spacing={2}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: fieldSurface.muted,
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Include
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {DIGEST_SECTION_KEYS.map((key) => (
                      <SelectableChip
                        key={key}
                        surface={fieldSurface}
                        label={SECTION_LABELS[key]}
                        selected={Boolean(alertForm.config.sections?.[key])}
                        onClick={() => toggleSection(key)}
                      />
                    ))}
                  </Stack>
                </Box>

                {/* AI briefing promo */}
                <Box
                  onClick={() => toggleSection("aiSummary")}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: aiEnabled
                      ? fieldSurface.accent
                      : (isDark ? alpha("#e2e8f0", 0.22) : alpha("#0f172a", 0.12)),
                    background: aiEnabled
                      ? (isDark
                        ? "linear-gradient(135deg, rgba(226,232,240,0.14) 0%, rgba(15,23,42,0.85) 55%, rgba(2,6,23,0.95) 100%)"
                        : "linear-gradient(135deg, rgba(15,23,42,0.06) 0%, rgba(255,255,255,0.98) 45%, rgba(241,245,249,0.95) 100%)")
                      : (isDark
                        ? "linear-gradient(135deg, rgba(30,41,59,0.7) 0%, rgba(15,23,42,0.55) 100%)"
                        : "linear-gradient(135deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)"),
                    px: 2,
                    py: 1.75,
                    transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                    boxShadow: aiEnabled
                      ? (isDark
                        ? "0 0 0 1px rgba(255,255,255,0.06), 0 14px 32px -18px rgba(0,0,0,0.7)"
                        : "0 12px 28px -16px rgba(15,23,42,0.28)")
                      : "none",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: fieldSurface.accent,
                      boxShadow: isDark
                        ? "0 0 0 1px rgba(255,255,255,0.06), 0 14px 32px -18px rgba(0,0,0,0.7)"
                        : "0 12px 28px -16px rgba(15,23,42,0.22)",
                    },
                    "@keyframes aiPromoShine": {
                      "0%": { transform: "translateX(-120%) skewX(-16deg)" },
                      "100%": { transform: "translateX(220%) skewX(-16deg)" },
                    },
                    "&::after": !aiEnabled ? {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "40%",
                      height: "100%",
                      background: isDark
                        ? "linear-gradient(105deg, transparent 20%, rgba(226,232,240,0.08) 50%, transparent 80%)"
                        : "linear-gradient(105deg, transparent 20%, rgba(15,23,42,0.05) 50%, transparent 80%)",
                      animation: "aiPromoShine 3.2s ease-in-out infinite",
                      pointerEvents: "none",
                    } : undefined,
                    "@media (prefers-reduced-motion: reduce)": {
                      "&::after": { animation: "none" },
                    },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "12px",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          color: isDark ? "#0f172a" : "#fff",
                          backgroundImage: isDark
                            ? "linear-gradient(135deg, #e2e8f0 0%, #f8fafc 55%, #cbd5e1 100%)"
                            : "linear-gradient(135deg, #0f172a 0%, #1e293b 48%, #000000 100%)",
                          boxShadow: isDark
                            ? "0 8px 20px -10px rgba(0,0,0,0.6)"
                            : "0 8px 20px -10px rgba(15,23,42,0.5)",
                        }}
                      >
                        <AutoAwesomeIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", letterSpacing: "-0.01em" }}>
                            AI briefing
                          </Typography>
                          <Chip
                            size="small"
                            label={aiEnabled ? "On" : "New"}
                            sx={{
                              height: 22,
                              fontWeight: 800,
                              fontSize: "0.68rem",
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              bgcolor: aiEnabled
                                ? (isDark ? alpha("#4ade80", 0.18) : alpha("#16a34a", 0.12))
                                : (isDark ? alpha("#e2e8f0", 0.16) : alpha("#0f172a", 0.08)),
                              color: aiEnabled
                                ? (isDark ? "#86efac" : "#15803d")
                                : fieldSurface.accent,
                              border: "none",
                            }}
                          />
                          {!aiEnabled && (
                            <Chip
                              size="small"
                              label="Try it now"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                bgcolor: "transparent",
                                border: "1px solid",
                                borderColor: fieldSurface.fieldBorder,
                                color: fieldSurface.muted,
                              }}
                            />
                          )}
                        </Stack>
                        <Typography
                          sx={{
                            mt: 0.45,
                            fontSize: "0.8rem",
                            color: fieldSurface.muted,
                            lineHeight: 1.4,
                            maxWidth: 460,
                          }}
                        >
                          {aiEnabled
                            ? "A short AI summary will open each report — highlights, risks, and what needs attention."
                            : "Add a sharp written brief on top of the numbers. One tap to try this new feature."}
                        </Typography>
                      </Box>
                    </Stack>
                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      control={
                        <Switch
                          checked={aiEnabled}
                          onChange={() => toggleSection("aiSummary")}
                          size="small"
                          sx={switchSx}
                        />
                      }
                      label={aiEnabled ? "Enabled" : "Off"}
                      sx={{
                        m: 0,
                        flexShrink: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: fieldSurface.muted,
                        },
                      }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: fieldSurface.muted,
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Channels
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    <SelectableChip
                      surface={fieldSurface}
                      label="Email"
                      selected={alertForm.channels.includes("email")}
                      onClick={() => toggleChannel("email")}
                    />
                    <SelectableChip
                      surface={fieldSurface}
                      label="In-app notification"
                      selected={alertForm.channels.includes("in_app")}
                      onClick={() => toggleChannel("in_app")}
                    />
                  </Stack>
                </Box>
              </Stack>
            </FormSection>

            <FormSection
              surface={fieldSurface}
              icon={<PeopleAltIcon fontSize="small" />}
              title="Recipients"
              subtitle="At least one person or mailing list"
            >
              <Stack spacing={2}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={userOptions.filter((u) => alertForm.recipientUserIds.includes(u.id))}
                  onChange={(_, v) =>
                    setAlertForm((f) => ({ ...f, recipientUserIds: v.map((u) => u.id) }))
                  }
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="People"
                      placeholder="Add teammates"
                      sx={fieldSx}
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  options={groups}
                  value={groups.filter((g) => alertForm.recipientGroupIds.includes(g.id))}
                  onChange={(_, v) =>
                    setAlertForm((f) => ({ ...f, recipientGroupIds: v.map((g) => g.id) }))
                  }
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Groups"
                      placeholder="Add mailing lists"
                      sx={fieldSx}
                    />
                  )}
                />
              </Stack>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 1.75 },
            gap: 1,
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& > :not(style) ~ :not(style)": { ml: { xs: 0, sm: 1 } },
            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <Button onClick={() => setAlertDialogOpen(false)} disabled={busy} sx={textBtnSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveAlert}
            disabled={busy}
            sx={{ ...solidBtnSx, minHeight: { xs: 44, sm: 36 }, px: 2.25 }}
          >
            {busy ? "Saving…" : "Save report"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group dialog */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => !busy && setGroupDialogOpen(false)}
        fullWidth
        fullScreen={isMobile}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "18px",
            border: "1px solid",
            borderColor: fieldSurface.panelBorder,
            backgroundImage: isDark
              ? "linear-gradient(160deg, #1e293b 0%, #0f172a 45%, #020617 100%)"
              : "linear-gradient(160deg, #ffffff 0%, #f8fafc 52%, #f1f5f9 100%)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: "-0.02em", pr: isMobile ? 6 : 3, position: "relative" }}>
          {editingGroup ? "Edit group" : "New group"}
          {isMobile && (
            <IconButton
              aria-label="Close"
              onClick={() => !busy && setGroupDialogOpen(false)}
              disabled={busy}
              sx={{ position: "absolute", right: 8, top: 10 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: fieldSurface.panelBorder }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={groupForm.name}
              onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))}
              sx={fieldSx}
            />
            <TextField
              label="Description"
              fullWidth
              value={groupForm.description}
              onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))}
              sx={fieldSx}
            />
            <Autocomplete
              multiple
              options={userOptions}
              value={userOptions.filter((u) => groupForm.memberIds.includes(u.id))}
              onChange={(_, v) =>
                setGroupForm((f) => ({ ...f, memberIds: v.map((u) => u.id) }))
              }
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Members" sx={fieldSx} />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            gap: 1,
            flexDirection: { xs: "column-reverse", sm: "row" },
            "& > :not(style) ~ :not(style)": { ml: { xs: 0, sm: 1 } },
            "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <Button onClick={() => setGroupDialogOpen(false)} disabled={busy} sx={textBtnSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveGroup}
            disabled={busy}
            sx={{ ...solidBtnSx, minHeight: { xs: 44, sm: 36 } }}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
